<template>
    <div class="ocp">
        <div class="ocp-toolbar">
            <span class="ocp-count">נבחרו {{ modelValue.length }} מתוך {{ totalCount }}</span>
            <div class="ocp-toolbar-actions">
                <button type="button" class="ocp-link" @click="selectAll">בחר הכל</button>
                <button type="button" class="ocp-link" @click="clearAll">נקה</button>
            </div>
        </div>

        <div v-for="group in groups" :key="group.id" class="ocp-group">
            <div v-if="group.label" class="ocp-group-head">
                <span class="ocp-group-title">{{ group.label }}</span>
                <span class="ocp-group-count">{{ selectedInGroup(group) }}/{{ group.options.length }}</span>
                <button type="button" class="ocp-link" @click="selectGroup(group)">בחר הכל</button>
                <button type="button" class="ocp-link" @click="clearGroup(group)">נקה</button>
            </div>
            <div class="ocp-row">
                <button
                    v-for="opt in group.options"
                    :key="opt.id"
                    type="button"
                    class="ocp-chip"
                    :class="{ selected: isSelected(opt.id) }"
                    :aria-pressed="isSelected(opt.id) ? 'true' : 'false'"
                    @click="toggle(opt.id)"
                >
                    <span v-if="isSelected(opt.id)" class="ocp-chip-mark" aria-hidden="true">✓</span>
                    {{ opt.label }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { computed } from 'vue'

/**
 * Admin multi-select chip picker: which options an ad offers to candidates.
 * - `groups`:     [{ id, label, options: [{ id, label }] }] (label may be empty)
 * - `modelValue`: array of selected option ids
 */
export default {
    name: 'OptionChipPicker',
    props: {
        groups: { type: Array, default: () => [] },
        modelValue: { type: Array, default: () => [] }
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
        const allIds = computed(() => props.groups.flatMap(g => g.options.map(o => o.id)))
        const totalCount = computed(() => allIds.value.length)

        const isSelected = (id) => props.modelValue.includes(id)
        const selectedInGroup = (group) => group.options.filter(o => isSelected(o.id)).length

        // Always emit ids in canonical (master) order so the saved list is stable
        const emitOrdered = (ids) => {
            const set = new Set(ids)
            emit('update:modelValue', allIds.value.filter(id => set.has(id)))
        }

        const toggle = (id) => {
            emitOrdered(isSelected(id) ? props.modelValue.filter(x => x !== id) : [...props.modelValue, id])
        }
        const selectAll = () => emitOrdered(allIds.value)
        const clearAll = () => emitOrdered([])
        const selectGroup = (group) => emitOrdered([...props.modelValue, ...group.options.map(o => o.id)])
        const clearGroup = (group) => {
            const groupIds = new Set(group.options.map(o => o.id))
            emitOrdered(props.modelValue.filter(id => !groupIds.has(id)))
        }

        return { totalCount, isSelected, selectedInGroup, toggle, selectAll, clearAll, selectGroup, clearGroup }
    }
}
</script>

<style scoped>
.ocp {
    display: flex;
    flex-direction: column;
    gap: 12px;
}
.ocp-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    flex-wrap: wrap;
}
.ocp-toolbar-actions {
    display: flex;
    gap: 4px;
}
.ocp-count {
    font-size: 12px;
    color: #777;
    font-weight: 600;
}
.ocp-link {
    background: none;
    border: none;
    color: #1579F2;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    padding: 2px 6px;
    border-radius: 6px;
    font-family: inherit;
}
.ocp-link:hover {
    background: #EBF4FF;
}
.ocp-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.ocp-group-head {
    display: flex;
    align-items: center;
    gap: 6px;
}
.ocp-group-title {
    font-size: 13px;
    font-weight: 700;
    color: #333;
}
.ocp-group-count {
    font-size: 11px;
    color: #999;
    margin-left: auto;
}
.ocp-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.ocp-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 6px 12px;
    border-radius: 20px;
    border: 1.5px solid #ccc;
    background: #fff;
    color: #333;
    font-size: 13px;
    font-weight: 600;
    font-family: inherit;
    line-height: 1.2;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s, color 0.15s;
}
.ocp-chip:hover {
    border-color: #1579F2;
    background: #f0f6ff;
}
.ocp-chip.selected {
    background: #1579F2;
    border-color: #1579F2;
    color: #fff;
}
.ocp-chip-mark {
    font-size: 11px;
    font-weight: 900;
}
</style>
